import { useQueries } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { FolderOpen, Eye, MessageSquare, Pencil, ShieldCheck, Download, ExternalLink, Users } from 'lucide-react'
import { getPermissions, getProjectOwners } from '../../services/api'
import { useAuthedFetch } from '../../hooks/useAuthedFetch'
import { useAuth } from '../../contexts/AuthContext'
import { personLabel } from '../../components/project/personLabel'
import type { ProjectOwner, Project } from '../../types'
import LoadingSkeleton from '../../components/shared/LoadingSkeleton'
import EmptyState from '../../components/ui/EmptyState'
import PageHeader from '../../components/ui/PageHeader'
import ContextualTip from '../../components/ui/ContextualTip'
import HelpPopover from '../../components/help/HelpPopover'
import StatusChip from '../../components/ui/StatusChip'
import { findMyGrant, accessBadge, type AccessLevel } from './accessLabels'
import { shortDate } from '../../lib/shortDate'

const BADGE_ICONS: Record<AccessLevel, JSX.Element> = {
  view: <Eye size={12} />,
  comment: <MessageSquare size={12} />,
  edit: <Pencil size={12} />,
  manager: <ShieldCheck size={12} />,
  admin: <ShieldCheck size={12} />,
}

/** Real owner display — never fabricated. Prefers the resolved owner record
 * (email via getProjectOwners); falls back to the raw owner_id through the same
 * granteeLabel helper used everywhere else so it's never a bare UUID. */
export function ownerDisplay(owners: ProjectOwner[] | undefined) {
  // Bug 6773: an owner with no email printed "User 69b17bc3", an id shown as if it were a person,
  // while the Atlas says "owner not named" for the same project. One honest label for both.
  // The owners route answers `email: email or owner_id`, so an owner with no known email arrives with
  // their id in the email field: only a value that really is an email counts.
  const primary = owners?.[0]
  const name = primary?.name?.trim()
  if (name) return { label: name, initial: name.charAt(0).toUpperCase() }
  if (primary?.email && primary.email.includes('@')) {
    // Bug #6681: a person is named, never shown as a raw login (personLabel turns the email's local part into a name).
    const label = personLabel({ email: primary.email })
    return { label, initial: (label.charAt(0) || '?').toUpperCase() }
  }
  return { label: 'Owner not named', initial: '?' }
}

export default function SharedProjects() {
  const navigate = useNavigate()
  const { user } = useAuth()
  // B1-03 root-cause fix: fetch the project list through useAuthedFetch (a raw
  // `fetch` that re-renders via useState) instead of the shared axios `api`
  // instance. An authenticated axios GET can reach XHR readyState 4 / status 200
  // with the full body yet NEVER settle its promise, so react-query's `isLoading`
  // stayed true forever and the skeleton spun indefinitely (retry/isError cannot
  // rescue a promise that never rejects). The direct-fetch hook is immune — the
  // same proven fix the 5 admin pages already use. Raw envelope: GET /projects →
  // { projects: [...] }.
  const { data, isLoading, isError, refetch } = useAuthedFetch<{ projects: Project[] }>('/projects')
  // BUG-2: list_projects returns owned + shared projects. This page is
  // "Shared with me", so show only the projects shared TO the user (flagged
  // shared_with_me by list_projects) — never the user's own projects.
  const projects = data?.projects?.filter((p) => p.shared_with_me)

  // Per-row live lookups for the permission table (Fig51): the grantee's actual
  // permission record (for the View/Comment/Download badge) and the project's
  // real owner (for the Owner column). Both real endpoints already used
  // elsewhere in the app (SharedDocumentView, ProjectView) — never fabricated.
  const permissionQueries = useQueries({
    queries: (projects ?? []).map((p) => ({
      queryKey: ['shared-project-permissions', p.project_id],
      queryFn: () => getPermissions(p.project_id),
      enabled: !!projects?.length,
    })),
  })
  const ownerQueries = useQueries({
    queries: (projects ?? []).map((p) => ({
      queryKey: ['shared-project-owners', p.project_id],
      queryFn: () => getProjectOwners(p.project_id),
      enabled: !!projects?.length,
    })),
  })

  if (isLoading) return <LoadingSkeleton rows={6} type="table" />

  // B1-03: an honest, actionable failure state — never an infinite skeleton and
  // never a false "nothing shared" when the fetch actually errored.
  if (isError) {
    return (
      <div>
        <PageHeader title="Shared with me" description="Projects others have shared with you." helpKey="shared" />
        <EmptyState
          icon={FolderOpen}
          title="Couldn't load shared projects"
          description="Something went wrong fetching the projects shared with you. Please try again."
          action={
            <button
              onClick={() => refetch()}
              className="px-4 py-2 bg-iflow-700 text-white text-sm font-medium rounded-lg hover:bg-iflow-800 transition-colors"
            >
              Retry
            </button>
          }
        />
      </div>
    )
  }

  return (
    <div>
      <PageHeader
        title="Shared with me"
        description="Projects others have shared with you. What you can see and do depends on the access each owner gave you."
        helpKey="shared"
      />

      {!projects?.length ? (
        <EmptyState
          icon={FolderOpen}
          title="Nothing shared with you yet"
          description="When someone shares a project with you, it appears here — and you'll see only what your access allows."
        />
      ) : (
        <>
          <ContextualTip id="shared-projects-ladder" tone="info">
            Each owner decides how much of a project you can see. If you need
            more, ask them to raise your access.{' '}
            <HelpPopover topic="concept-disclosure-ladder" size={14} />
          </ContextualTip>

          <p className="mt-3 text-xs text-gray-500">
            {projects.length} project{projects.length === 1 ? '' : 's'} shared with you
          </p>

          <div className="mt-2 light-card overflow-hidden p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-iflow-800 text-white text-xs uppercase tracking-wide">
                    <th className="text-left px-4 py-3 font-semibold">Item</th>
                    <th className="text-left px-4 py-3 font-semibold">Owner</th>
                    <th className="text-left px-4 py-3 font-semibold">Type</th>
                    <th className="text-left px-4 py-3 font-semibold">Permission</th>
                    <th className="text-left px-4 py-3 font-semibold">Shared</th>
                    <th className="text-right px-4 py-3 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {projects.map((project, idx) => {
                    const permissions = permissionQueries[idx]?.data
                    const myPermission = findMyGrant(permissions, user)
                    const owners = ownerQueries[idx]?.data
                    const owner = ownerDisplay(owners)
                    const badge = accessBadge(myPermission, { loading: !!permissionQueries[idx]?.isLoading })

                    return (
                      <tr key={project.project_id} className="hover:bg-iflow-50/40 transition-colors">
                        <td className="px-4 py-3">
                          <button
                            onClick={() => navigate(`/shared/project/${project.project_id}`)}
                            className="flex items-center gap-2 text-left group"
                          >
                            <FolderOpen size={16} className="text-iflow-500 flex-shrink-0" />
                            <span className="min-w-0">
                              <span className="block font-medium text-gray-900 group-hover:text-iflow-600 truncate">{project.name}</span>
                              <span className="block text-xs text-gray-500">{project.document_count} docs</span>
                            </span>
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-iflow-100 text-iflow-700 text-[10px] font-semibold flex-shrink-0">
                              {owner.initial}
                            </span>
                            <span className="text-gray-700 truncate">{owner.label}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <StatusChip tone="neutral">Project</StatusChip>
                        </td>
                        <td className="px-4 py-3">
                          {badge.state === 'loading' ? (
                            <span
                              role="status"
                              aria-label="Loading your access"
                              className="inline-block h-5 w-16 animate-pulse rounded-full bg-slate-200"
                            />
                          ) : (
                            <span className="flex flex-wrap items-center gap-1">
                              <StatusChip
                                tone={badge.tone}
                                icon={badge.level ? BADGE_ICONS[badge.level] : <Users size={12} />}
                              >
                                {badge.label}
                              </StatusChip>
                              {badge.canDownload && (
                                <StatusChip tone="success" icon={<Download size={12} />}>Can download</StatusChip>
                              )}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-500">
                          {myPermission?.created_at ? shortDate(myPermission.created_at) : '—'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => navigate(`/shared/project/${project.project_id}`)}
                              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-iflow-700 border border-iflow-200 rounded-lg hover:bg-iflow-50"
                            >
                              <ExternalLink size={12} /> Open
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
